/**
 * Editor de la matriz de permisos por rol para un tenant desde el admin
 * web. Complementa (no reemplaza) al editor del desktop — el superadmin
 * puede editar los overrides desde aquí sin necesidad de entrar al POS
 * del tenant.
 *
 * Modelo: el catálogo y los defaults viven en `@atreus/core` y se
 * computan client-side. El worker solo almacena overrides. La matriz
 * efectiva es `default ∪ overrides`.
 *
 * Layout mimetiza el de desktop: tabs por módulo, toggles por celda,
 * botón Reset por celda modificada, reset bulk por rol. La diferencia
 * principal es que acá el superadmin siempre puede editar — no hay
 * gating por `settings.editPermissions`.
 */

import {
  DEFAULT_PERMISSION_MATRIX,
  USER_ROLES,
  permissions as permissionCatalog,
  type PermissionDefinition,
  type PermissionId,
  type UserRole,
} from '@atreus/core';
import { Button } from '@atreus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';

import { apiDelete, apiGet, apiPatch } from '../../cloud.js';

const ROLE_LABELS: Record<UserRole, string> = {
  superadmin: 'Super-admin',
  admin: 'Administrador',
  manager: 'Encargado',
  cashier: 'Cajero',
  waiter: 'Mesero',
};

const MODULE_ORDER: PermissionDefinition['module'][] = [
  'orders',
  'products',
  'categories',
  'cash',
  'payments',
  'customers',
  'tables',
  'delivery',
  'tips',
  'inventory',
  'stats',
  'expenses',
  'settings',
  'users',
];

const MODULE_LABELS: Record<PermissionDefinition['module'], string> = {
  orders: 'Pedidos',
  products: 'Productos',
  categories: 'Categorías',
  cash: 'Caja',
  payments: 'Pagos',
  customers: 'Clientes y cartera',
  tables: 'Mesas',
  delivery: 'Domicilios',
  tips: 'Propinas',
  inventory: 'Inventario',
  stats: 'Estadísticas',
  expenses: 'Gastos',
  settings: 'Configuraciones',
  users: 'Usuarios',
};

interface Override {
  role: UserRole;
  permissionId: string;
  granted: boolean;
}

interface OverridesResponse {
  overrides: Override[];
}

export const PermissionsMatrix = ({ tenantId }: { tenantId: string }) => {
  const qc = useQueryClient();
  const queryKey = ['admin', 'tenants', tenantId, 'permissions'];
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey });
  };

  const overridesQuery = useQuery({
    queryKey,
    queryFn: () =>
      apiGet<OverridesResponse>(`/admin/tenants/${tenantId}/permissions`),
  });

  const setOverride = useMutation({
    mutationFn: (vars: {
      role: UserRole;
      permissionId: PermissionId;
      granted: boolean;
    }) =>
      apiPatch<{ granted: boolean; overridden: boolean }>(
        `/admin/tenants/${tenantId}/permissions`,
        vars,
      ),
    onSuccess: invalidate,
  });

  const resetOverride = useMutation({
    mutationFn: (vars: { role: UserRole; permissionId: PermissionId }) =>
      apiDelete<{ ok: true }>(
        `/admin/tenants/${tenantId}/permissions/${vars.role}/${encodeURIComponent(
          vars.permissionId,
        )}`,
      ),
    onSuccess: invalidate,
  });

  const resetAllForRole = useMutation({
    mutationFn: (vars: { role: UserRole }) =>
      apiDelete<{ ok: true; deleted: number }>(
        `/admin/tenants/${tenantId}/permissions/${vars.role}`,
      ),
    onSuccess: invalidate,
  });

  // Agrupa catálogo por módulo respetando el orden preferido.
  const modulesGrouped = useMemo(() => {
    const perms = Object.values(permissionCatalog);
    const byModule = new Map<PermissionDefinition['module'], PermissionDefinition[]>();
    for (const p of perms) {
      const arr = byModule.get(p.module) ?? [];
      arr.push(p);
      byModule.set(p.module, arr);
    }
    const orderedKeys: PermissionDefinition['module'][] = [
      ...MODULE_ORDER.filter((m) => byModule.has(m)),
      ...Array.from(byModule.keys()).filter((m) => !MODULE_ORDER.includes(m)),
    ];
    return orderedKeys.map((m) => ({
      module: m,
      permissions: byModule.get(m) ?? [],
    }));
  }, []);

  const [activeModule, setActiveModule] = useState<PermissionDefinition['module']>(
    'orders',
  );

  // Lookup rápido `${role}::${permissionId}` → override (si existe).
  const overrideLookup = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const o of overridesQuery.data?.overrides ?? []) {
      m.set(`${o.role}::${o.permissionId}`, o.granted);
    }
    return m;
  }, [overridesQuery.data]);

  const editableRoles: UserRole[] = USER_ROLES.filter(
    (r) => r !== 'superadmin',
  );

  if (overridesQuery.isLoading) {
    return <div className="text-fg-muted">Cargando matriz de permisos…</div>;
  }
  if (overridesQuery.error) {
    return (
      <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
        {(overridesQuery.error as Error).message}
      </p>
    );
  }

  const activeEntry =
    modulesGrouped.find((g) => g.module === activeModule) ?? modulesGrouped[0];
  const activePermissions = activeEntry?.permissions ?? [];
  const mutationError =
    setOverride.error ?? resetOverride.error ?? resetAllForRole.error;
  const busy =
    setOverride.isPending ||
    resetOverride.isPending ||
    resetAllForRole.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-fg-muted">
        <Info className="mt-0.5 size-4 shrink-0" />
        <span>
          Cada celda decide si ese rol puede ejecutar la acción en este
          tenant. Un punto <span className="text-accent">•</span> al lado
          significa que se cambió respecto al valor de fábrica. El botón
          "Reset" devuelve al default.
        </span>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {modulesGrouped.map((g) => {
          const active = g.module === activeModule;
          return (
            <button
              key={g.module}
              type="button"
              onClick={() => setActiveModule(g.module)}
              className={
                'rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ' +
                (active
                  ? 'border-fg bg-fg text-fg-inverse'
                  : 'border-border bg-surface text-fg-muted hover:text-fg')
              }
            >
              {MODULE_LABELS[g.module]}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-muted text-left text-xs uppercase tracking-wider text-fg-muted">
            <tr>
              <th className="min-w-48 px-3 py-2 font-semibold">Permiso</th>
              {editableRoles.map((role) => (
                <th
                  key={role}
                  className="w-32 px-3 py-2 text-center font-semibold"
                >
                  {ROLE_LABELS[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activePermissions.map((perm) => (
              <tr key={perm.id} className="border-t border-border">
                <td className="px-3 py-2 align-top">
                  <div className="font-semibold">{perm.name}</div>
                  <div className="mt-0.5 text-xs text-fg-muted">
                    {perm.description}
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] text-fg-subtle">
                    {perm.id}
                  </div>
                </td>
                {editableRoles.map((role) => {
                  const permId = perm.id as PermissionId;
                  const override = overrideLookup.get(`${role}::${permId}`);
                  const defaultGranted =
                    DEFAULT_PERMISSION_MATRIX[role].has(permId);
                  const granted = override !== undefined ? override : defaultGranted;
                  const overridden = override !== undefined;
                  return (
                    <td
                      key={role}
                      className="px-3 py-2 text-center align-top"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            setOverride.mutate({
                              role,
                              permissionId: permId,
                              granted: !granted,
                            })
                          }
                          className={
                            'relative inline-flex h-6 w-11 items-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
                            (granted
                              ? 'border-success/50 bg-success/20'
                              : 'border-border bg-surface-muted')
                          }
                          aria-pressed={granted}
                          aria-label={`${perm.name} · ${ROLE_LABELS[role]}: ${granted ? 'concedido' : 'denegado'}`}
                        >
                          <span
                            className={
                              'inline-block size-4 rounded-full shadow-sm transition-transform ' +
                              (granted
                                ? 'translate-x-6 bg-success'
                                : 'translate-x-1 bg-fg-muted')
                            }
                          />
                        </button>
                        {overridden ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              resetOverride.mutate({
                                role,
                                permissionId: permId,
                              })
                            }
                            className="flex items-center gap-1 text-[10px] text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                            title="Volver al valor por defecto para este rol"
                          >
                            <RotateCcw className="size-2.5" />
                            Reset
                          </button>
                        ) : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mutationError ? (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {(mutationError as Error).message}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4 text-xs">
        <span className="text-fg-muted">
          Resetear todos los permisos de un rol a los valores de fábrica:
        </span>
        {editableRoles.map((role) => (
          <Button
            key={role}
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => resetAllForRole.mutate({ role })}
          >
            <RotateCcw className="size-3.5" />
            {ROLE_LABELS[role]}
          </Button>
        ))}
      </div>
    </div>
  );
};
